/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {UserManagementService} from '../../../../services/user-management.service';
import {ToastService} from '@siemens/ix-angular';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router} from '@angular/router';
import {User} from '../../../../models/User';
import {Subscription} from 'rxjs';
import {UserService} from '../../../../services/user.service';

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrl: './edit-user.component.scss',
  standalone: false,
})
export class EditUserComponent implements OnInit, OnDestroy {
  private readonly userManagementService = inject(UserManagementService);
  private readonly userService = inject(UserService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private routeSubscription?: Subscription;

  loadingWhileFetching = true;
  loadingWhileSaving = false;
  targetRoute = 'app/list-user';

  userData: User | undefined = {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    organization: {
      id: '',
      name: '',
    },
    language: '',
    region: '',
    status: '',
  };

  editUser?: User;

  userId = '';
  cancelEvent = new EventEmitter<void>();

  async ngOnInit() {
    this.routeSubscription = this.route.params.subscribe(async (params) => {
      this.userId = params['id'];
      if (this.userId) {
        this.editUser = await this.userManagementService.loadUser(this.userId);
        if (this.editUser) {
          this.userData = this.editUser;
        } else {
          this.translateService
            .get('um.user_not_found', {id: this.userId})
            .subscribe((msg: string) => {
              this.toastService.show({message: msg, type: 'error'});
            });
        }
        this.loadingWhileFetching = false;
      }
    });
  }

  ngOnDestroy() {
    this.routeSubscription?.unsubscribe();
  }

  async saveUser(changedUserData: User) {
    this.loadingWhileSaving = true;
    try {
      const deletedRoles = this.editUser?.roles?.filter(
        (item) => !changedUserData.roles?.includes(item),
      );
      const addedRoles = changedUserData?.roles?.filter(
        (role) => !this.editUser?.roles?.find((item) => item === role),
      );
      if (deletedRoles && deletedRoles.length > 0) {
        const mappedRoles = deletedRoles.map((item) => item);
        await this.userManagementService.deleteRolesForUser(
          this.userId,
          mappedRoles,
        );
      }
      if (addedRoles && addedRoles.length > 0) {
        const mappedRoles = addedRoles.map((item) => item);
        await this.userManagementService.updateRolesForUser(
          this.userId,
          mappedRoles,
        );
      }
      const successMessage = this.translateService.instant(
        'settings.user_changed',
      );
      await this.toastService.show({message: successMessage, type: 'success'});
      await this.router.navigate([this.targetRoute]);
    } catch {
      const errorMessage = this.translateService.instant(
        'settings.user_update_failed',
        {user: changedUserData.id},
      );
      await this.toastService.show({message: errorMessage, type: 'error'});
    }
    this.loadingWhileSaving = false;
  }

  async onBackButtonClick() {
    this.cancelEvent.emit();
  }
}
